import { useMemo } from "react"
import {
    Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts"
import { ProjectHealthPeriod, ProjectHealthUI } from "models/project-health"
import { formatCurrency } from "utils/tools"

// Series colours are CSS tokens (light/dark) defined in _project-health.scss
const ACTUAL = "var(--health-series-actual)"
const FORECAST = "var(--health-series-forecast)"
const PLANNED = "var(--health-series-planned)"
const REFERENCE = "var(--health-reference)"

const AXIS_TICK = { fill: "var(--bs-secondary-color)", fontSize: 12 }
const GRID = "var(--bs-border-color)"

const compactCurrency = (v: number) =>
    new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", notation: "compact", maximumFractionDigits: 1 }).format(v)

const tooltipStyle = {
    contentStyle: { background: "var(--bs-body-bg)", border: "1px solid var(--bs-border-color)", borderRadius: 8 },
    labelStyle: { color: "var(--bs-emphasis-color)", fontWeight: 600 },
    itemStyle: { color: "var(--bs-body-color)" },
}

interface ChartProps {
    series: ProjectHealthPeriod[]
    currentPeriod: number
}

// Monthly revenue: planned vs actual (closed periods) vs forecast (current period onward).
export const ProjectMonthlyRevenueChart = ({ series, currentPeriod }: ChartProps) => {
    const data = useMemo(() => series.map(s => ({
        label: ProjectHealthUI.periodLabel(s.fiscalPeriod),
        planned: s.planned,
        actual: s.fiscalPeriod < currentPeriod ? s.actual : null,
        forecast: s.fiscalPeriod >= currentPeriod ? s.forecast : null,
    })), [series, currentPeriod])

    return (
        <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} barGap={2} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={GRID} strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={12} />
                    <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={compactCurrency} width={64} />
                    <Tooltip {...tooltipStyle} cursor={{ fill: "var(--bs-tertiary-bg)" }} formatter={(v: number) => formatCurrency(v, "CAD", 0)} />
                    <Legend wrapperStyle={{ color: "var(--bs-body-color)" }} />
                    <Bar dataKey="planned" name="Planned" fill={PLANNED} radius={[4, 4, 0, 0]} maxBarSize={18} />
                    <Bar dataKey="actual" name="Actual" fill={ACTUAL} radius={[4, 4, 0, 0]} maxBarSize={18} />
                    <Bar dataKey="forecast" name="Forecast" fill={FORECAST} radius={[4, 4, 0, 0]} maxBarSize={18} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    )
}

interface CumulativeProps extends ChartProps {
    budget: number
}

// Cumulative revenue against the Income budget. Projection = actuals to date, then forecast.
export const ProjectCumulativeRevenueChart = ({ series, currentPeriod, budget }: CumulativeProps) => {
    const data = useMemo(() => series.map(s => ({
        label: ProjectHealthUI.periodLabel(s.fiscalPeriod),
        planned: s.cumulativePlanned,
        actual: s.fiscalPeriod < currentPeriod ? s.cumulativeActual : null,
        projected: s.cumulativeProjected,
    })), [series, currentPeriod])

    return (
        <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 16, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={GRID} strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={12} />
                    <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={compactCurrency} width={64}
                        domain={[0, (max: number) => Math.max(max, budget) * 1.05]} />
                    <Tooltip {...tooltipStyle} formatter={(v: number) => formatCurrency(v, "CAD", 0)} />
                    <Legend wrapperStyle={{ color: "var(--bs-body-color)" }} />
                    {budget > 0 && (
                        <ReferenceLine y={budget} stroke={REFERENCE} strokeDasharray="6 4" strokeWidth={2}
                            label={{ value: `Income budget ${compactCurrency(budget)}`, position: "insideTopLeft", fill: "var(--bs-secondary-color)", fontSize: 12 }} />
                    )}
                    <Line type="monotone" dataKey="planned" name="Planned" stroke={PLANNED} strokeWidth={2} strokeDasharray="4 3" dot={false} activeDot={{ r: 4 }} />
                    <Line type="monotone" dataKey="projected" name="Actual + forecast" stroke={FORECAST} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                    <Line type="monotone" dataKey="actual" name="Actual" stroke={ACTUAL} strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls={false} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    )
}

// Hours charged to the project per month (Activity timesheets).
export const ProjectHoursChart = ({ series }: { series: ProjectHealthPeriod[] }) => {
    const data = useMemo(() => series
        .filter(s => s.hours > 0 || s.billableHours > 0)
        .map(s => ({ label: ProjectHealthUI.periodLabel(s.fiscalPeriod), hours: s.hours, billable: s.billableHours })), [series])

    if (data.length === 0) return <p className="text-muted mb-0">No timesheet hours recorded for this project.</p>

    return (
        <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} barGap={2} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={GRID} strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={12} />
                    <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={48} />
                    <Tooltip {...tooltipStyle} cursor={{ fill: "var(--bs-tertiary-bg)" }} formatter={(v: number) => `${v.toLocaleString("en-CA")} h`} />
                    <Legend wrapperStyle={{ color: "var(--bs-body-color)" }} />
                    <Bar dataKey="hours" name="Total hours" fill={ACTUAL} radius={[4, 4, 0, 0]} maxBarSize={18} />
                    <Bar dataKey="billable" name="Billable hours" fill={FORECAST} radius={[4, 4, 0, 0]} maxBarSize={18} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    )
}
